import { useCallback, useEffect, useState } from "react";

import {
    listTenantSlaAction,
    getTenantSlaByIdAction,
    deleteTenantSlaAction,
    updateTenantSlaStatusAction,
} from "../../Actions/TenantSlaActions";

import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import AddEditTenantSla from "./AddEditTenantSla";
import DataTable from "../../components/DataTable/DataTable";

import { ITEMS_PER_PAGE_OPTIONS } from "../../config";

const TenantSlaSection = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);

    const [openModal, setOpenModal] = useState(false);
    const [editData, setEditData] = useState(null);

    const [selectedRowsPerPage, setSelectedRowsPerPage] =
        useState(ITEMS_PER_PAGE_OPTIONS[0]);

    const [filterValues, setFilterValues] = useState({
        search: "",
        isActive: "all",
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleAdd = () => {
        setEditData(null);
        setOpenModal(true);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const res = await listTenantSlaAction();

            let slaData = res?.data?.data || [];

            if (filterValues.search) {
                const search = filterValues.search.toLowerCase();

                slaData = slaData.filter((item) =>
                    item?.slaName?.toLowerCase().includes(search)
                );
            }

            if (filterValues.isActive !== "all") {
                const activeValue =
                    filterValues.isActive === "true";

                slaData = slaData.filter(
                    (item) =>
                        Boolean(item?.isActive) === activeValue
                );
            }

            setData(slaData);
        } catch (error) {
            setSnackbar({
                open: true,
                message:
                    error?.response?.data?.message ||
                    "Error fetching SLA data",
                severity: "error",
            });
        }

        setLoading(false);
    }, [filterValues]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDelete = async (row) => {
        try {
            await deleteTenantSlaAction(row.id);

            setSnackbar({
                open: true,
                message: "Tenant SLA deleted successfully",
                severity: "success",
            });

            fetchData();
        } catch (error) {
            const status = error?.response?.status;

            setSnackbar({
                open: true,
                message:
                    error?.response?.data?.message ||
                    "Something went wrong",
                severity: status >= 500 ? "error" : "info",
            });
        }
    };

    const handleEdit = async (id) => {
        try {
            const res = await getTenantSlaByIdAction(id);

            setEditData(res?.data?.data || res?.data);
            setOpenModal(true);
        } catch (error) {
            setSnackbar({
                open: true,
                message:
                    error?.response?.data?.message ||
                    "Failed to fetch SLA details",
                severity: "error",
            });
        }
    };

    const handleStatusToggle = async (row) => {
        try {
            const newStatus = !row.isActive;

            await updateTenantSlaStatusAction(row.id, {
                isActive: newStatus,
            });

            setSnackbar({
                open: true,
                message: `Tenant SLA ${newStatus ? "activated" : "deactivated"
                    } successfully`,
                severity: "success",
            });

            fetchData();
        } catch (error) {
            setSnackbar({
                open: true,
                message:
                    error?.response?.data?.message ||
                    "Something went wrong",
                severity: "error",
            });
        }
    };

    const columns = [
        {
            field: "slaName",
            headerName: "SLA Name",
            flex: 1,
            minWidth: 200,
            renderCell: ({ row }) =>
                row?.slaName || "-",
        },

        {
            field: "description",
            headerName: "Description",
            flex: 1.5,
            minWidth: 250,
            renderCell: ({ row }) =>
                row?.description || "-",
        },

        {
            field: "priority",
            headerName: "Priority",
            flex: 0.8,
            minWidth: 120,
            renderCell: ({ row }) =>
                row?.priority
                    ? row.priority.charAt(0).toUpperCase() +
                    row.priority.slice(1)
                    : "-",
        },

        {
            field: "stages",
            headerName: "Stages",
            flex: 0.7,
            minWidth: 100,
            renderCell: ({ row }) =>
                Array.isArray(row?.stages)
                    ? row.stages.length
                    : 0,
        },

        {
            field: "isActive",
            headerName: "Status",
            flex: 0.8,
            minWidth: 100,
            renderCell: ({ row }) =>
                row?.isActive ? "Active" : "Inactive",
        },
    ];

    return (
        <div>
            <BreadcrumbsComponent />

            <DataTable
                rows={data}
                columns={columns}
                statusField="isActive"
                loading={loading}
                totalCount={data.length}
                page={currentPage}
                pageSize={selectedRowsPerPage}
                getRowId={(row) => row.id}
                onPageChange={(page) =>
                    setCurrentPage(page)
                }
                onPageSizeChange={(size) => {
                    setSelectedRowsPerPage(size);
                    setCurrentPage(1);
                }}
                filterValues={filterValues}
                onFilterApply={(values) => {
                    setFilterValues(values);
                    setCurrentPage(1);
                }}
                onFilterReset={() => {
                    setFilterValues({
                        search: "",
                        isActive: "all",
                    });

                    setCurrentPage(1);
                }}
                filters={[
                    {
                        type: "search",
                        placeholder: "Search SLA...",
                    },
                    {
                        type: "select",
                        name: "isActive",
                        label: "Status",
                        options: [
                            {
                                label: "All",
                                value: "all",
                            },
                            {
                                label: "Active",
                                value: "true",
                            },
                            {
                                label: "Inactive",
                                value: "false",
                            },
                        ],
                    },
                ]}
                actions={{
                    edit: true,
                    delete: true,
                    activate: true,
                    deactivate: true,
                }}
                onAction={(action, row) => {
                    if (action === "edit") {
                        handleEdit(row.id);
                    }

                    if (action === "delete") {
                        handleDelete(row);
                    }

                    if (
                        action === "activate" ||
                        action === "deactivate"
                    ) {
                        handleStatusToggle(row);
                    }
                }}
                bulkActions={{
                    delete: false,
                }}
                hasCreatePermission={true}
                onAddClick={handleAdd}
            />

            {openModal && (
                <AddEditTenantSla
                    open={openModal}
                    handleClose={() => {
                        setOpenModal(false);
                        setEditData(null);
                    }}
                    editData={editData}
                    setSnackbar={setSnackbar}
                    refresh={fetchData}
                />
            )}

            <CustomSnackbar
                {...snackbar}
                onClose={() =>
                    setSnackbar((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
            />
        </div>
    );
};

export default TenantSlaSection;